import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { GradeHandWritingDocument } from "@/generated/graphql";
import { useCanvasDrawing } from "@/lib/hooks/slides/editor/useCanvasDrawing";
import { useAppSelector } from "@/store/hooks";
import { baselineFor, traceFontSize } from "@ai-teach/canvas-core";
import type { HandWritingSlideData } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { IoArrowForward, IoCheckmark, IoTrashOutline, IoVolumeHigh } from "react-icons/io5";
import { HandWritingTraceGuide } from "./HandWritingTraceGuide";
import { WRITING_FONT } from "./handWritingLayout";

type Phase = "practice" | "test" | "submitted";

/** The title mirrors the board: traced ink, the pen's orange, the road still to be written. */
const LETTER_COLOR = { done: "#2626A5", current: "#FF8500", todo: "#C7D2E8" };

/** A prompt can be a single letter or a whole word, so the display shrinks to stay on screen. */
const wordFontSize = (word: string) => {
  if (word.length <= 1) return 72;
  if (word.length <= 4) return 56;
  if (word.length <= 8) return 40;
  if (word.length <= 14) return 28;
  return 22;
};

const scoreTone = (score: number) => {
  if (score >= 85) return { color: "#2FA84F", label: "Excellent!" };
  if (score >= 60) return { color: "#FF8500", label: "Nice work!" };
  return { color: "#E5533D", label: "Keep practicing!" };
};

export function HandWritingLiveMobileView() {
  const [phase, setPhase] = useState<Phase>("practice");
  const [textIndex, setTextIndex] = useState(0);
  const [scoreFeedback, setScoreFeedback] = useState<null | { score: number; feedback: string }>(
    null
  );
  const [gradingFailed, setGradingFailed] = useState(false);
  const [trace, setTrace] = useState({ completed: 0, total: 0, done: false, charIndex: 0 });
  const [traceKey, setTraceKey] = useState(0);
  const [board, setBoard] = useState({ width: 0, height: 0 });

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const { text, textAudios } = slide?.slideData as HandWritingSlideData;

  const currentText = text[textIndex] ?? "";
  const hasNextText = textIndex < text.length - 1;
  const wordAudioSrc = textAudios?.[textIndex]?.src ?? null;

  const boardRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wordAudioRef = useRef<HTMLAudioElement>(null);
  const playedWordRef = useRef<number | null>(null);

  useEffect(() => {
    const element = boardRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setBoard({ width, height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const playWordAudio = useCallback(() => {
    const el = wordAudioRef.current;
    if (!el) return;
    el.currentTime = 0;
    void el.play().catch(() => {});
  }, []);

  // The reward for finishing the guided trace: the student hears the word they just wrote.
  useEffect(() => {
    if (phase !== "practice" || !trace.done || !wordAudioSrc) return;
    if (playedWordRef.current === textIndex) return;
    playedWordRef.current = textIndex;
    playWordAudio();
  }, [phase, trace.done, textIndex, wordAudioSrc, playWordAudio]);

  const { hasInk, toBlob, handleClear, handlePointerDown, handlePointerMove, handlePointerUp } =
    useCanvasDrawing(canvasRef);

  const [gradeHandWritingMutation, { loading }] = useMutation(GradeHandWritingDocument);

  const gradeHandWriting = async () => {
    setGradingFailed(false);

    try {
      const blob = await toBlob();
      const file = new File([blob], `handwriting-${slide?.id}-${textIndex}.png`, {
        type: "image/png",
      });

      const response = await gradeHandWritingMutation({
        variables: {
          image: file,
          text: currentText,
        },
      });

      if (response.data?.gradeHandWriting) {
        setScoreFeedback(response.data?.gradeHandWriting);
      } else {
        setGradingFailed(true);
      }
    } catch {
      setGradingFailed(true);
    }
  };

  const clearWritingSpace = () => {
    handleClear();
    setTrace({ completed: 0, total: 0, done: false, charIndex: 0 });
    setTraceKey((key) => key + 1);
    playedWordRef.current = null;
    wordAudioRef.current?.pause();
  };

  const restart = (nextIndex: number) => {
    setTextIndex(nextIndex);
    setScoreFeedback(null);
    setGradingFailed(false);
    clearWritingSpace();
    setPhase("practice");
  };

  const tone = scoreTone(scoreFeedback?.score ?? 0);
  // Gated on strokes actually being laid out, so a font that never loads can't trap the student here.
  const tracePending = phase === "practice" && trace.total > 0 && !trace.done;
  const actionReady = phase === "practice" ? !tracePending : hasInk;

  const baselineY = baselineFor(board, traceFontSize(board));

  const letterColor = (index: number) => {
    if (phase !== "practice" || index < trace.charIndex) return LETTER_COLOR.done;
    return index === trace.charIndex ? LETTER_COLOR.current : LETTER_COLOR.todo;
  };

  const drawingHandlers =
    phase === "practice"
      ? {}
      : {
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerUp,
          onPointerCancel: handlePointerUp,
        };

  return (
    <LiveMobileViewContainer backgroundColor="#FFFFFF">
      <div className="w-full flex-1 min-h-0 flex flex-col gap-2 px-4 pb-4">
        <div className="relative shrink-0 pt-2 flex flex-col items-center justify-center">
          <p
            className="text-center font-bold leading-none break-words"
            style={{ fontSize: wordFontSize(currentText), fontFamily: WRITING_FONT }}
          >
            {currentText ? (
              [...currentText].map((char, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: letters repeat, their position is the identity
                <span key={`${char}-${index}`} style={{ color: letterColor(index) }}>
                  {char}
                </span>
              ))
            ) : (
              <span className="text-2xl text-muted-foreground">No word yet</span>
            )}
          </p>

          {wordAudioSrc && (
            <>
              {/* biome-ignore lint/a11y/useMediaCaption: the clip is the word read aloud */}
              <audio ref={wordAudioRef} src={wordAudioSrc} preload="auto" />
              <button
                type="button"
                onClick={playWordAudio}
                aria-label="Play the word"
                className="absolute right-0 bottom-0 w-10 h-10 rounded-full flex items-center justify-center bg-[#EEF2FF] hover:brightness-95 transition-all"
              >
                <IoVolumeHigh className="text-xl" style={{ color: "#2626A5" }} />
              </button>
            </>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-col gap-1.5">
          <div className="flex items-center justify-between px-1">
            <span
              className="text-[11px] font-bold uppercase tracking-widest"
              style={{ color: "#AFAFAF" }}
            >
              {phase === "practice" && trace.total > 0
                ? `Stroke ${Math.min(trace.completed + 1, trace.total)} of ${trace.total}`
                : "Writing space"}
            </span>
            <button
              type="button"
              onClick={clearWritingSpace}
              aria-label="Clear writing"
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors hover:brightness-95"
              style={{ backgroundColor: "#FFF0ED", color: "#E5533D" }}
            >
              <IoTrashOutline className="text-sm" />
              Clear
            </button>
          </div>

          <div
            ref={boardRef}
            className="relative flex-1 min-h-0 rounded-[28px] overflow-hidden bg-white"
            style={{ border: "1px solid #E4EAF5" }}
          >
            <canvas
              ref={canvasRef}
              {...drawingHandlers}
              className="absolute inset-0 w-full h-full touch-none"
            />
            {/* Guides sit above the canvas so they never end up in the graded image. */}
            {/* biome-ignore lint/a11y/noSvgWithoutTitle: the baseline is decoration, the word is already shown above */}
            <svg className="pointer-events-none absolute inset-0 w-full h-full">
              <line
                x1={24}
                x2={board.width - 24}
                y1={baselineY}
                y2={baselineY}
                stroke="#CBD9EF"
                strokeWidth={2}
              />
            </svg>
            {/* Practice reveals the real glyph instead of the child's own line, so a trace always matches. */}
            {phase === "practice" && currentText !== "" && (
              <HandWritingTraceGuide key={traceKey} text={currentText} onProgress={setTrace} />
            )}
          </div>
        </div>

        {/* One slot: the instruction stands in for the button until the student has done what it asks. */}
        <div className="h-[54px] shrink-0 flex items-center justify-center">
          {actionReady ? (
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                if (phase === "practice") {
                  setPhase("test");
                  clearWritingSpace();
                } else {
                  gradeHandWriting();
                  setPhase("submitted");
                }
              }}
              className="w-full h-[54px] rounded-full flex items-center justify-center gap-2 text-white font-bold text-lg tracking-wide transition-all enabled:hover:opacity-95 enabled:active:scale-[0.99] disabled:opacity-40"
              style={{ background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)" }}
            >
              {phase === "practice" ? "Continue" : "Submit"}
              {phase === "practice" ? (
                <IoArrowForward className="text-xl" />
              ) : (
                <IoCheckmark className="text-xl" />
              )}
            </button>
          ) : (
            <div className="rounded-[20px] px-4 py-2 max-w-[90%] bg-blue-700 text-white">
              <p className="text-center text-[15px] font-medium leading-snug">
                {phase === "practice" ? "Follow the arrow." : "Your turn! Write it yourself! ✏️"}
              </p>
            </div>
          )}
        </div>
      </div>

      {phase === "submitted" && (
        <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/40">
          <div className="w-full rounded-t-[32px] bg-white px-6 pt-7 pb-8 flex flex-col items-center gap-4 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="w-10 h-10 rounded-full border-[3px] border-t-transparent animate-spin border-[#FFB800]" />
                <p className="text-[15px] font-semibold" style={{ color: "#565656" }}>
                  Checking your writing…
                </p>
              </div>
            ) : gradingFailed ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <p className="text-lg font-bold" style={{ color: "#E5533D" }}>
                  Something went wrong
                </p>
                <p className="text-center text-[15px]" style={{ color: "#565656" }}>
                  We couldn't check your writing. Give it another try.
                </p>
              </div>
            ) : (
              <>
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center"
                  style={{
                    background: `conic-gradient(${tone.color} ${(scoreFeedback?.score ?? 0) * 3.6}deg, #EFF1F6 0deg)`,
                  }}
                >
                  <div className="w-[78px] h-[78px] rounded-full bg-white flex items-center justify-center">
                    <span className="text-[32px] font-extrabold" style={{ color: tone.color }}>
                      {scoreFeedback?.score ?? 0}
                    </span>
                  </div>
                </div>
                <p className="text-xl font-bold" style={{ color: tone.color }}>
                  {tone.label}
                </p>
                <p
                  className="text-center text-[15px] leading-snug px-2"
                  style={{ color: "#565656" }}
                >
                  {scoreFeedback?.feedback}
                </p>
              </>
            )}

            {!loading && (
              <div className="w-full flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => restart(textIndex)}
                  className="flex-1 h-[50px] rounded-full bg-white font-bold text-base hover:bg-orange-50 transition-colors"
                  style={{ border: "1.5px solid #FFD08A", color: "#FF8500" }}
                >
                  Try again
                </button>
                {hasNextText && (
                  <button
                    type="button"
                    onClick={() => restart(textIndex + 1)}
                    className="flex-1 h-[50px] rounded-full text-white font-bold text-base flex items-center justify-center gap-1.5 hover:opacity-95 transition-opacity"
                    style={{
                      background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)",
                      boxShadow: "0 6px 16px rgba(255, 133, 0, 0.32)",
                    }}
                  >
                    Next word
                    <IoArrowForward className="text-lg" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </LiveMobileViewContainer>
  );
}
